import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const STORAGE_KEY = "click-photography-cookie-consent";

export const CookieConsent = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const choice = localStorage.getItem(STORAGE_KEY);
    if (!choice) {
      // Small delay so it doesn't pop up during hero animation
      const id = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(id);
    }
  }, []);

  const save = (value) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, ts: new Date().toISOString() })
      );
    } catch (e) {
      // ignore localStorage errors
    }
    setVisible(false);
    // Notify the rest of the app so analytics can boot if needed
    window.dispatchEvent(new CustomEvent("cookie-consent", { detail: value }));
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-50 bg-white border border-border-soft shadow-[0_20px_60px_rgba(0,0,0,0.12)] fade-up"
    >
      <div className="p-6 md:p-7">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-bg-alt flex items-center justify-center text-gold shrink-0">
              <Cookie size={18} />
            </div>
            <h3 className="font-serif text-xl md:text-2xl text-accent-dark font-light leading-tight">
              {t.cookies.title}
            </h3>
          </div>
          <button
            onClick={() => save("rejected")}
            aria-label="Dismiss"
            data-testid="cookie-dismiss"
            className="text-text-muted hover:text-accent-dark transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <p className="mt-4 text-sm text-text-muted leading-relaxed">
          {t.cookies.body}
        </p>

        <div className="mt-5 flex items-center gap-3">
          <button
            data-testid="cookie-accept"
            onClick={() => save("accepted")}
            className="btn-luxury"
            style={{ padding: "0.7rem 1.4rem", fontSize: "0.65rem" }}
          >
            {t.cookies.accept}
          </button>
          <button
            data-testid="cookie-reject"
            onClick={() => save("rejected")}
            className="text-[11px] tracking-[0.2em] uppercase font-medium text-text-muted hover:text-accent-dark transition-colors"
          >
            {t.cookies.reject}
          </button>
        </div>

        <Link
          to="/privacy#cookies"
          data-testid="cookie-learn-more"
          className="mt-4 inline-block text-[11px] tracking-[0.2em] uppercase text-gold hover:text-accent-dark transition-colors underline-offset-4 hover:underline"
        >
          {t.cookies.learnMore} →
        </Link>
      </div>
    </div>
  );
};
